import React from 'react'
import styled from 'styled-components'

import ColumnGridPanel from 'components/ColumnGridPanel'
import Tooltip from 'components/Tooltip'

import ConnectedStatusIcon from 'pages/shared/ConnectedStatusIcon'

import { color } from 'styles/colors'
import { FRIENDLY_NAMES } from 'helpers/reportFriendlyNames'
import { degreesFahrenheit, friendlyName, relativeHumidity } from 'helpers/formatters'

import useServerlessDemandResponse from 'hooks/useServerlessDemandResponse'
import RenderFirmwareStatus from 'pages/shared/RenderFirmwareStatus'
import { useStoreContext } from 'hooks/StoreContext'

type ReportProps = {
  systemMode: string
  operatingStatus: string
  outdoorTemperature: string
  emergencyHeatEnabled: boolean
  dehumidifierStatus: string
  ventilatorStatus: string
  quietModeAvailable: boolean
  quietModeActive: boolean
  relativeHumidity: number
  firmwareVersion?: string
  heatingOperatingStages?: {
    indoorOperatingStage: number
  }
}

type Props = {
  report: ReportProps
  hasZones: boolean
  deviceId: string
  deviceModel?: string
}

const systemStatusIcons = {
  'OFF': 'off.png',
  'SYSTEM_COOLING': 'cooling.png',
  'SYSTEM_HEATING': 'heating.svg',
  'DEFROST': 'heating-defrost.svg',
  'FAN_RUNNING': 'fan.svg'
}

const Status = ({ report, hasZones = false, deviceModel, deviceId }: Props) => {
  const { demandResponse } = useServerlessDemandResponse(deviceId, true)
  const { defaultFirmwareLoads, areDefaultFirmwareLoadsLoaded } = useStoreContext()

  const emergencyHeatOperating = !!(report.emergencyHeatEnabled &&
    report.heatingOperatingStages?.indoorOperatingStage &&
    report.heatingOperatingStages.indoorOperatingStage > 0)

  const demandResponseTooltip = demandResponse && demandResponse.currentlyActive
    ? 'System is actively in a Utility Savings (Demand Response) event'
    : 'System is currently not in a Utility Savings (Demand Response) event'

  return (
    <S.Container>
      {!hasZones &&
        <div>
          <span>System Mode: </span> <S.Status>{report.systemMode ? friendlyName(report.systemMode) : '--'}</S.Status>
        </div>
      }
      <S.StatusRow>
        <span>Status:</span>
        {
          report.operatingStatus &&
          <S.Status>{emergencyHeatOperating ? 'Emergency Heating' : friendlyName(report.operatingStatus.replace('SYSTEM_', ''), FRIENDLY_NAMES)}</S.Status>
        }
        { report.operatingStatus && systemStatusIcons[report.operatingStatus as keyof typeof systemStatusIcons] && !emergencyHeatOperating &&
          <S.StatusIcon src={`/img/status-icons/${systemStatusIcons[report.operatingStatus as keyof typeof systemStatusIcons]}`} alt="system status" />
        }
        {
          emergencyHeatOperating &&
          <S.StatusIcon src="/img/status-icons/emergency-heat.png" alt="emergency-heat" />
        }
        {
          report.dehumidifierStatus &&
          report.dehumidifierStatus.toLowerCase() !== 'not installed' &&
          report.dehumidifierStatus.toLowerCase() !== 'off' &&
          <S.StatusIcon src="/img/status-icons/dehumidifying.svg" alt="dehumidifying-on" />
        }
        {
          report.ventilatorStatus &&
          report.ventilatorStatus.toLowerCase() !== 'not installed' &&
          report.ventilatorStatus.toLowerCase() !== 'off' &&
          <S.StatusIcon src="/img/status-icons/ventilator.svg" alt="ventilator-on" />
        }
        {report.quietModeAvailable && report.quietModeActive && <S.StatusIcon src="/img/status-icons/quitemode.svg" alt="quitemode-enabled" /> }
      </S.StatusRow>
      {demandResponse && demandResponse.enrollmentStatus &&
        <S.DemandResponseRow>
          <span>Utility Savings Event:</span>
          <Tooltip title={demandResponseTooltip}>
            <S.Status>{demandResponse.currentlyActive ? 'ACTIVE' : 'NOT ACTIVE'}</S.Status>
          </Tooltip>
        </S.DemandResponseRow>
      }
      {report.firmwareVersion && deviceModel && (
        <S.FirmwareRow>
          <S.FirmwareLabel>Firmware:</S.FirmwareLabel>
          <RenderFirmwareStatus
            modelNumber={deviceModel}
            currentVersion={report.firmwareVersion}
            defaultFirmwareLoads={defaultFirmwareLoads}
            areDefaultFirmwareLoadsLoaded={areDefaultFirmwareLoadsLoaded}
            component="status"
          />
        </S.FirmwareRow>
      )}
      {hasZones &&
        <>
          <S.OutdoorTemp><span>Outdoor Temp: </span>{degreesFahrenheit(report.outdoorTemperature) || '--'}</S.OutdoorTemp>
          <S.RelativeHumidity><span>Relative Humidity: </span>{relativeHumidity(report.relativeHumidity) || '--'}</S.RelativeHumidity>
        </>
      }
    </S.Container>
  )
}

export default Status

const S = {
  ConnectedStatusIcon: styled(ConnectedStatusIcon)`
    margin-left: 5px;
    width: 16px;
    height: 16px;
  `,
  DemandResponseRow: styled.div`
    margin-top: 5px;
  `,
  OutdoorTemp: styled.div`
    margin-top: 5px;
  `,
  RelativeHumidity: styled.div`
    margin-top: 5px;
    font-size: 1rem;
  `,
  StatusRow: styled.div`
    display: flex;
    align-items: center;
    margin-top: 5px;
    gap: 5px;
  `,
  FirmwareRow: styled.div`
    margin-top: 5px;
    display: flex;
    align-items: center;
  `,
  Status: styled.span`
    color: ${color('LastUpdatedText')};
    text-transform: capitalize;
    margin-left: 5px;
`,
  StatusIcon: styled.img`
    margin-left: 5px;
    width: 25px;
    height: 25px;
  `,
  Item: styled(ColumnGridPanel.Column.Item)`
    grid-template-columns: 45% 55%;
  `,
  Container: styled.div`
    display: flex;
    flex-direction: column;
    margin: 0 0 10px 0;
    font-size: 14px;
    line-height: 1.5;

    span {
      font-weight: 600;
    }
  `,
  FirmwareLabel: styled.span`
    margin-right: 5px;
  `
}
